/**
 * EntityListPage — instantiate the Items list template for any entity.
 *
 * Stack: EntityListShell + EntityListBody + ListTable (same table as Items).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { CollectionView } from './CollectionView';
import { EntityListShell } from './EntityListShell';
import { EntityListBody } from './EntityListBody';
import { ListTable } from './ListTable';
import { useCollectionState } from './useCollectionState';
import type {
  FacetDefinition,
  SortOption,
  GroupOption,
  TableColumnWithRender,
  ViewMode,
  BulkAction,
} from './types';
import type { EntityDensityMode } from './EntityListToolbar';
import { InlineBulkActions } from './BulkActionToolbar';
import { EmptyState } from '../EmptyState';
import { pluralizeEntityLabel } from './pluralizeEntityLabel';
import { ListTiles, type ListTileTone } from './ListTiles';
import { MOBILE_MEDIA_QUERY } from '../../mobile/featureFlag';

/**
 * On a phone a list opens as cards when it draws its own card, as the items list
 * does (useItemListState). A list with no card of its own keeps its table.
 */
function opensAsCards(viewModes: ViewMode[], hasCard: boolean): boolean {
  if (!hasCard || !viewModes.includes('card')) return false;
  try {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * A count across the top of the list. With `match` it is also a filter: pressing
 * it shows only the rows it counts. Without, it counts every row and reads only.
 */
export interface EntityListTile<T> {
  id: string;
  label: string;
  match?: (item: T) => boolean;
  tone?: ListTileTone | ((count: number) => ListTileTone);
  hint?: ReactNode;
}

export interface EntityListPageProps<T, SortField extends string = string> {
  items: T[];
  loading?: boolean;
  getId: (item: T) => string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  iconBgClassName?: string;
  searchFields?: (keyof T)[];
  searchPlaceholder?: string;
  sortOptions: SortOption<SortField>[];
  defaultSort?: { field: SortField; direction: 'asc' | 'desc' };
  groupOptions?: GroupOption<string>[];
  facets?: FacetDefinition<string>[];
  tableColumns?: TableColumnWithRender<T>[];
  renderCard?: (item: T, ctx: { selected: boolean }) => ReactNode;
  renderItem?: (item: T) => ReactNode;
  /** Slide-in detail panel (Items pattern). Default true when renderDetail provided. */
  detailPanel?: boolean;
  /** @deprecated Use detailPanel. When false, no slide-in panel. */
  masterDetail?: boolean;
  selectedId?: string | null;
  onSelectionChange?: (id: string | null) => void;
  renderDetail?: (item: T) => ReactNode;
  detailPanelWidth?: number;
  /** @deprecated URL sync handled by parent; kept for API compat. */
  urlParamName?: string;
  onRefresh?: () => void;
  onImport?: () => void;
  onExport?: () => void;
  onCreate?: () => void;
  createLabel?: string;
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  secondaryActions?: ReactNode;
  headerActions?: ReactNode;
  headerExtra?: ReactNode;
  afterToolbar?: ReactNode;
  contextBar?: { label: string; action?: { label: string; onClick: () => void } };
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: { label: string; onClick: () => void };
  error?: string | null;
  itemLabel?: string;
  viewModes?: ViewMode[];
  defaultViewMode?: ViewMode;
  onItemClick?: (item: T) => void;
  showChart?: boolean;
  onToggleChart?: () => void;
  onManageReports?: () => void;
  selectable?: boolean;
  bulkActions?: BulkAction[];
  bulkActionsBar?: ReactNode;
  onReorderColumns?: (order: string[]) => void;
  className?: string;
  shellClassName?: string;
  /** Class on list-mode CollectionView content wrapper */
  contentClassName?: string;
  /** Optional trailing row actions (Items row menu pattern) */
  renderRowActions?: (item: T) => ReactNode;
  /** Counts across the top; a tile with `match` filters the list (Decisions' tiles). */
  tiles?: EntityListTile<T>[];
  /** The tile pressed now, when the page keeps it (in the URL). Uncontrolled otherwise. */
  activeTileId?: string | null;
  onActiveTileChange?: (id: string | null) => void;
  /** A row's peek in the table: a few facts and a link to the record's page. */
  renderPeek?: (item: T) => ReactNode;
}

export function EntityListPage<T, SortField extends string = string>({
  items,
  loading = false,
  getId,
  title,
  subtitle,
  icon,
  iconBgClassName = 'bg-[var(--slate-dim)]',
  searchFields = [],
  searchPlaceholder,
  sortOptions,
  defaultSort,
  groupOptions = [],
  facets = [],
  tableColumns = [],
  renderCard,
  renderItem,
  detailPanel,
  masterDetail = true,
  selectedId = null,
  onSelectionChange,
  renderDetail,
  detailPanelWidth = 380,
  urlParamName: _urlParamName,
  onRefresh,
  onImport,
  onExport,
  onCreate,
  createLabel = 'New',
  primaryAction,
  secondaryActions,
  headerActions,
  headerExtra,
  afterToolbar,
  contextBar,
  emptyTitle,
  emptyDescription,
  emptyAction,
  error,
  itemLabel = 'record',
  viewModes = ['table', 'card'],
  defaultViewMode,
  onItemClick,
  showChart,
  onToggleChart,
  onManageReports,
  selectable: selectableProp,
  bulkActions = [],
  bulkActionsBar,
  onReorderColumns,
  className = 'h-full min-h-0',
  shellClassName = 'h-full',
  contentClassName,
  renderRowActions,
  tiles,
  activeTileId: activeTileIdProp,
  onActiveTileChange,
  renderPeek,
}: EntityListPageProps<T, SortField>) {
  const [ownTileId, setOwnTileId] = useState<string | null>(null);
  const activeTileId = activeTileIdProp !== undefined ? activeTileIdProp : ownTileId;
  const setActiveTile = useCallback(
    (id: string | null) => {
      setOwnTileId(id);
      onActiveTileChange?.(id);
    },
    [onActiveTileChange],
  );
  const activeTile = tiles?.find((t) => t.id === activeTileId && t.match);
  // The rows the list works on: all of them, or the ones the pressed tile counts.
  const scopedItems = useMemo(
    () => (activeTile?.match ? items.filter(activeTile.match) : items),
    [items, activeTile],
  );
  // No rows: the named empty state speaks; a row of zeros above it says nothing (§1E, zero).
  const tilesNode = tiles && tiles.length > 0 && items.length > 0 ? (
    <div className="px-4 md:px-6 py-3 flex-shrink-0">
      <ListTiles
        activeId={activeTile ? activeTile.id : null}
        onSelect={setActiveTile}
        tiles={tiles.map((t) => {
          const count = t.match ? items.filter(t.match).length : items.length;
          return {
            id: t.id,
            label: t.label,
            value: count,
            hint: t.hint,
            filters: Boolean(t.match),
            tone: typeof t.tone === 'function' ? t.tone(count) : t.tone,
          };
        })}
      />
    </div>
  ) : null;

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [density, setDensity] = useState<EntityDensityMode>('default');
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const resolvedDefaultView = opensAsCards(viewModes, Boolean(renderCard))
    ? 'card'
    : defaultViewMode ?? (viewModes.includes('table') ? 'table' : viewModes[0] ?? 'list');
  const [layoutMode, setLayoutMode] = useState<ViewMode>(resolvedDefaultView);
  const resolvedSelectable = selectableProp ?? tableColumns.length > 0;
  const useDetailPanel =
    detailPanel ?? (masterDetail && Boolean(renderDetail && onSelectionChange));

  const columnKeys = useMemo(() => tableColumns.map((c) => c.key), [tableColumns]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => columnKeys);

  useEffect(() => {
    setVisibleColumns((prev) => {
      const next = prev.filter((k) => columnKeys.includes(k));
      return next.length > 0 ? next : columnKeys;
    });
  }, [columnKeys]);

  const [state, actions] = useCollectionState<T, SortField, string, string>({
    items: scopedItems,
    getId,
    defaultViewMode: layoutMode,
    searchFields,
    facets,
    sortOptions,
    defaultSort,
    groupOptions,
    enableSelection: resolvedSelectable,
  });

  useEffect(() => {
    actions.setViewMode(layoutMode);
  }, [layoutMode, actions]);

  const visibleTableColumns = useMemo(
    () =>
      visibleColumns
        .map((key) => tableColumns.find((c) => c.key === key))
        .filter((c): c is TableColumnWithRender<T> => Boolean(c)),
    [tableColumns, visibleColumns],
  );

  const toggleColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        return prev.length > 1 ? prev.filter((k) => k !== key) : prev;
      }
      return [...prev, key];
    });
  }, []);

  const handleReorderColumns = useCallback(
    (order: string[]) => {
      setVisibleColumns(order);
      onReorderColumns?.(order);
    },
    [onReorderColumns],
  );

  const removeFilter = useCallback(
    (filterId: string) => {
      actions.setFilters(state.activeFilters.filter((f) => f.id !== filterId));
    },
    [actions, state.activeFilters],
  );

  const bulkSelectedCount = state.selection.selectedIds.size;

  const handleSelectAllFiltered = useCallback(() => {
    actions.selection.selectAll();
  }, [actions]);

  const defaultCardRender = useCallback(
    (item: T, { selected }: { selected: boolean }) => (
      <div className="p-4">
        <p className="font-medium truncate text-sm">
          {String(
            (item as Record<string, unknown>).title ??
              (item as Record<string, unknown>).name ??
              getId(item),
          )}
        </p>
        {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
      </div>
    ),
    [getId],
  );

  const resolvedRenderCard = renderCard ?? defaultCardRender;

  const resolvedRenderItem = useCallback(
    (item: T, ctx: { selected: boolean; onSelect: () => void; onToggle: () => void }) => {
      if (renderItem) return renderItem(item);
      return resolvedRenderCard(item, ctx);
    },
    [renderItem, resolvedRenderCard],
  );

  const handleLayoutModeChange = useCallback(
    (mode: ViewMode) => {
      setLayoutMode(mode);
      actions.setViewMode(mode);
    },
    [actions],
  );

  const selectedItem = selectedId ? items.find((i) => getId(i) === selectedId) : null;

  const resolvedPrimary =
    primaryAction ?? (onCreate ? { label: createLabel, onClick: onCreate } : undefined);

  const sortSpec = useMemo(
    () => [{ field: String(state.sort.field), direction: state.sort.direction }],
    [state.sort],
  );

  const handleSortChange = useCallback(
    (specs: { field: string; direction: 'asc' | 'desc' }[]) => {
      const s = specs[0];
      if (s) actions.setSort(s.field as SortField, s.direction);
    },
    [actions],
  );

  const listColumns = useMemo(
    () =>
      visibleTableColumns.map((col) => ({
        key: col.key,
        label: col.label,
        sortField: col.sortable ? col.key : false,
        defaultWidth: col.minWidth ?? (typeof col.width === 'number' ? col.width : 120),
        width: typeof col.width === 'string' ? col.width : undefined,
        align: col.align,
        flex: col.flex,
        render: (item: T) =>
          col.render(item, {
            selected: state.selection.selectedIds.has(getId(item)),
            onSelect: () => actions.selection.select(getId(item)),
            onToggle: () => actions.selection.toggle(getId(item)),
          }),
      })),
    [visibleTableColumns, getId, state.selection.selectedIds, actions.selection],
  );

  const groupedForTable = useMemo(() => {
    if (!state.groupedItems) return null;
    return state.groupedItems.map((g) => ({
      key: g.key,
      label: g.label,
      count: g.count,
      rows: g.items,
    }));
  }, [state.groupedItems]);

  const displayRows = state.groupedItems ? state.sortedItems : state.sortedItems;

  const handleRowClick = useCallback(
    (item: T) => {
      if (onItemClick) onItemClick(item);
      else if (onSelectionChange) onSelectionChange(getId(item));
    },
    [onItemClick, onSelectionChange, getId],
  );

  const bulkBar =
    bulkActionsBar ??
    (resolvedSelectable && bulkSelectedCount > 0 && bulkActions.length > 0 ? (
      <InlineBulkActions
        selectedCount={bulkSelectedCount}
        totalCount={state.filteredCount}
        actions={bulkActions}
        selectedIds={Array.from(state.selection.selectedIds)}
        onSelectAll={handleSelectAllFiltered}
        onClearSelection={actions.selection.deselectAll}
      />
    ) : null);

  const tableView =
    layoutMode === 'table' && listColumns.length > 0 ? (
      <ListTable
        rows={displayRows}
        getId={getId}
        columns={listColumns}
        density={density}
        selectedIds={state.selection.selectedIds}
        onToggleRow={(id) => actions.selection.toggle(id)}
        onSelectAll={(ids) => actions.selection.setSelected(ids)}
        onClearSelection={actions.selection.deselectAll}
        onRowClick={handleRowClick}
        sort={sortSpec}
        onSortChange={handleSortChange}
        selectedRowId={selectedId}
        grouped={groupedForTable}
        expandedGroups={state.group.expandedGroups}
        onToggleGroup={actions.toggleGroup}
        searchQuery={state.searchQuery}
        columnWidths={columnWidths}
        onColumnResize={(key, width) => setColumnWidths((prev) => ({ ...prev, [key]: width }))}
        onColumnResetWidth={(key) =>
          setColumnWidths((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          })
        }
        renderRowActions={renderRowActions}
        selectable={resolvedSelectable}
        renderPeek={renderPeek}
      />
    ) : null;

  const listView =
    layoutMode === 'list' && renderItem ? (
      <CollectionView
        items={scopedItems}
        getId={getId}
        hideToolbar
        hideStatsBar
        controlledState={state}
        controlledActions={actions}
        searchFields={searchFields}
        sortOptions={sortOptions}
        defaultSort={defaultSort}
        groupOptions={groupOptions}
        facets={facets}
        viewModes={['list']}
        defaultViewMode="list"
        selectable={resolvedSelectable}
        bulkActions={bulkActions}
        emptyVariant="generic"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
        onItemClick={handleRowClick}
        renderItem={resolvedRenderItem}
        className="flex-1 min-h-0 h-full"
        contentClassName={contentClassName ?? 'h-full'}
      />
    ) : null;

  const cardView =
    layoutMode === 'card' ? (
      <CollectionView
        items={scopedItems}
        getId={getId}
        hideToolbar
        hideStatsBar
        controlledState={state}
        controlledActions={actions}
        searchFields={searchFields}
        sortOptions={sortOptions}
        defaultSort={defaultSort}
        groupOptions={groupOptions}
        facets={facets}
        viewModes={['card']}
        defaultViewMode="card"
        selectable={resolvedSelectable}
        bulkActions={bulkActions}
        listStyle="cards"
        emptyVariant="generic"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyAction={emptyAction}
        onItemClick={handleRowClick}
        renderItem={resolvedRenderItem}
        renderCard={resolvedRenderCard}
        className="flex-1 min-h-0 h-full"
        contentClassName="h-full"
      />
    ) : null;

  const mainContent = loading && items.length === 0 ? (
    <div className="flex-1 flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
    </div>
  ) : state.filteredCount === 0 && items.length > 0 ? (
    // There are rows; the search, a filter or a tile hides them all. Say so, and offer them back.
    <div className="flex-1 flex items-center justify-center p-8">
      <EmptyState
        variant="generic"
        title={`No ${pluralizeEntityLabel(itemLabel)} match`}
        description={`${items.length} ${items.length === 1 ? itemLabel : pluralizeEntityLabel(itemLabel)} in all; what you have chosen above hides every one.`}
        actions={[
          {
            label: 'Show everything',
            onClick: () => {
              setActiveTile(null);
              actions.clearFilters();
              actions.setSearchQuery('');
            },
            primary: true,
          },
        ]}
      />
    </div>
  ) : state.filteredCount === 0 ? (
    <div className="flex-1 flex items-center justify-center p-8">
      <EmptyState
        variant="generic"
        title={emptyTitle}
        description={emptyDescription}
        actions={
          emptyAction
            ? [{ label: emptyAction.label, onClick: emptyAction.onClick, primary: true }]
            : undefined
        }
      />
    </div>
  ) : (
    tableView ?? listView ?? cardView
  );

  return (
    <div className={className}>
      <EntityListShell
        className={shellClassName}
        icon={icon}
        iconBgClassName={iconBgClassName}
        title={title}
        subtitle={subtitle}
        searchQuery={state.searchQuery}
        onSearchChange={actions.setSearchQuery}
        searchPlaceholder={searchPlaceholder ?? `Search ${pluralizeEntityLabel(itemLabel)}… ⌘K`}
        onRefresh={onRefresh}
        refreshing={loading}
        onImport={onImport}
        onExport={onExport}
        primaryAction={resolvedPrimary}
        secondaryActions={secondaryActions}
        headerActions={headerActions}
        headerExtra={
          headerExtra || tilesNode ? (
            <>
              {headerExtra}
              {tilesNode}
            </>
          ) : undefined
        }
        afterToolbar={afterToolbar}
        error={error}
        filterPanelOpen={filterPanelOpen}
        facets={facets}
        activeFilters={state.activeFilters}
        onFilterChange={actions.setFilters}
        onRemoveFilter={afterToolbar ? undefined : removeFilter}
        onClearFilters={afterToolbar ? undefined : actions.clearFilters}
        contextBar={contextBar}
        toolbar={{
          density,
          onDensityChange: setDensity,
          sortOptions: sortOptions.map((o) => ({ field: String(o.field), label: o.label })),
          sort: { field: String(state.sort.field), direction: state.sort.direction },
          onSortChange: (s) => actions.setSort(s.field as SortField, s.direction),
          groupOptions: groupOptions.map((o) => ({ field: String(o.field), label: o.label })),
          groupBy: state.group.field ? String(state.group.field) : null,
          onGroupByChange: (field) => actions.setGroupBy(field),
          filterCount: state.activeFilters.length,
          onToggleFilterPanel: () => setFilterPanelOpen((v) => !v),
          totalCount: state.itemCount,
          filteredCount: state.filteredCount,
          selectedCount: resolvedSelectable ? bulkSelectedCount : 0,
          onSelectAllFiltered: resolvedSelectable ? handleSelectAllFiltered : undefined,
          layoutMode:
            viewModes.includes('table') && viewModes.includes('card')
              ? (layoutMode === 'table' || layoutMode === 'card' ? layoutMode : 'table')
              : undefined,
          onLayoutModeChange:
            viewModes.includes('table') && viewModes.includes('card')
              ? handleLayoutModeChange
              : undefined,
          showChart,
          onToggleChart,
          onManageReports,
          columns: tableColumns.map((c) => ({ key: c.key, label: c.label })),
          visibleColumns,
          onToggleColumn: tableColumns.length > 0 ? toggleColumn : undefined,
          onReorderColumns: tableColumns.length > 0 ? handleReorderColumns : undefined,
          itemLabel,
        }}
      >
        {bulkBar}
        <EntityListBody
          detailPanelOpen={Boolean(selectedId)}
          detailPanelWidth={detailPanelWidth}
          detailPanelTitle={selectedItem ? title : undefined}
          onDetailPanelClose={() => onSelectionChange?.(null)}
          detailPanel={
            useDetailPanel && renderDetail && selectedId && selectedItem
              ? renderDetail(selectedItem)
              : undefined
          }
        >
          {mainContent}
        </EntityListBody>
      </EntityListShell>
    </div>
  );
}
